// SOR-625: the fixed mobile tab bar (<540px) — Browse · Following · Saved.
// Desktop never sees it (sm:hidden).
//
// Wiring maps tabs to what EXISTS today: Browse = the browse grid (filter
// 'all'), Saved = the existing saved filter, Following = the
// reminders-derived live signal (the dot). No new screens are invented
// here — see the ponytail notes in BrowseScreen. (SOR-626: the "Me"
// account/schedule tab was removed — no screen exists behind it.)
//
// Geometry: 76px border-box bar plus the home-indicator inset below it
// (h = 76px exactly when the inset is 0); BrowseScreen pads its content by
// the same 76px + inset so nothing is occluded. Translucent white with a
// 12px backdrop blur and a 1px top border. While the FilterSheet is open
// the bar sits UNDER its z-40 scrim (bar is z-30) and is marked inert so
// it can't take taps or focus.
//
// All colors come from the browse-* tokens (raw hex fails the designTokens
// jest check).
import React from 'react';
import posthog from 'posthog-js';
// direct @tabler/icons imports (not the icons.js barrel): the tab bar's
// 22/1.8 sizing is context-specific, not a design-handoff icon size
import { IconLayoutGrid, IconHeart, IconBookmark } from '@tabler/icons';

// single owner of the bar geometry: content that must clear the fixed bar
// pads by this exact class (BrowseScreen interpolates it, so the 76px +
// safe-area value lives in one file; the literal string keeps Tailwind's
// scanner emitting it)
export const TAB_BAR_CONTENT_PAD =
  'pb-[calc(76px_+_env(safe-area-inset-bottom))]';

const TABS = [
  ['browse', 'Browse', IconLayoutGrid],
  ['following', 'Following', IconHeart],
  ['saved', 'Saved', IconBookmark],
];

/**
 * @param {Object} props
 * @param {'browse'|'following'|'saved'} props.active
 * @param {(tab: string) => void} props.onSelect
 * @param {boolean} [props.followingLive]  a followed channel is live now —
 *   lights the dot on Following
 * @param {boolean} [props.inert]  true while the filter sheet is open
 */
const MobileTabBar = ({ active, onSelect, followingLive = false, inert = false }) => (
  <nav
    aria-label="Primary"
    inert={inert ? '' : undefined}
    className="fixed inset-x-0 bottom-0 z-30 box-border flex h-[calc(76px_+_env(safe-area-inset-bottom))] items-stretch border-t border-browse-divider bg-white/80 pb-[env(safe-area-inset-bottom)] backdrop-blur-[12px] sm:hidden"
  >
    {TABS.map(([id, label, Icon]) => (
      <button
        key={id}
        type="button"
        aria-current={active === id ? 'page' : undefined}
        onClick={() => {
          posthog.capture('browse_tab_selected', { tab: id });
          onSelect(id);
        }}
        className={`flex min-h-[44px] min-w-[44px] flex-1 flex-col items-center justify-center gap-1 font-inter-tight text-11 font-medium ${
          active === id ? 'text-browse-accent' : 'text-browse-text-secondary'
        }`}
      >
        <span className="relative">
          <Icon aria-hidden="true" size={22} stroke={1.8} />
          {id === 'following' && followingLive && (
            <span
              aria-hidden="true"
              className="absolute right-[-3px] top-[-2px] h-1.5 w-1.5 rounded-full bg-browse-live"
            />
          )}
        </span>
        {label}
        {id === 'following' && followingLive && (
          <span className="sr-only">(a channel you follow is live)</span>
        )}
      </button>
    ))}
  </nav>
);

export default MobileTabBar;
